import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { TranslateService } from '@ngx-translate/core';
import { Alert } from 'app/models/alert.model';
import { FaIconRaw } from 'app/shared/components/fa-icons/fa-icons.component';
import falist from 'assets/fa/falist.json';

@Component({
  selector: 'app-alerts-map',
  templateUrl: './alerts-map.component.html',
  styleUrls: ['./alerts-map.component.scss']
})
export class AlertsMapComponent implements OnInit, AfterViewInit {

  //Evènement de sélection d'une alerte sur la map
  @Output() alertSelected = new EventEmitter<Alert>();

  @Output() authCenterChanged = new EventEmitter<boolean>();

  //Input: Activer/Désactiver le clustering de markers
  @Input() clusterEnabled = true;

  //Input: Tableau des alertes à afficher sur la map
  @Input() alerts: Alert[] = [];

  //Input: Centrage de la map
  @Input() center: google.maps.LatLngLiteral;

  //Référence vers la GoogleMap
  @ViewChild(GoogleMap, { static: false })
  map: GoogleMap;

  //Cluster de markers
  cluster: MarkerClusterer;

  //Path vers les images de clusters
  markerClustererImagePath = "/assets/img/pins/m";

  //Tableau d'alerte en cache afin de comparer si il y a des changements
  alertsBeforeChange: Alert[] = [];

  //CONFIG
  GMAPS_MULTIPLIER: number = 0.3;
  zIndexCurrent = 0;

  //Options de la GoogleMap
  options = {
    zoom: 10,              //Zoom au chargement
    scrollwheel: true,    //Possibilité de ZOOM avec le scroll
    mapTypeId: 'roadmap', //hybrid ou roadmap
    heading: 90,
    tilt: 45,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    disableDoubleClickZoom: true,
    fullscreenControl: true
  }

  constructor(private translateService: TranslateService) { 
  }

  ngOnInit(): void {
  }

  /**
   * Lorsque la vue est chargée, on crée le cluster et on lance un "refresh" de tout les markers
   */
  ngAfterViewInit() {
    this.cluster = new MarkerClusterer(this.map?.googleMap, [], { 'imagePath': this.markerClustererImagePath });
    this.refreshMarkers();

    const centerControlDiv = document.createElement("div");
    this.addCustomControls(centerControlDiv, this.map?.googleMap);
    this.map?.googleMap.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(centerControlDiv);

  }

  /**
   * Sur changement des données d'entrées, on refresh les markers
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges) {
    if (this.alertsBeforeChange?.toString() !== this.alerts?.toString()) {
      this.refreshMarkers();
    }
    this.alertsBeforeChange = this.alerts;
  }

  /**
   * Fonction de refresh des markers
   */
  refreshMarkers() {

    //Clear all markers
    this.alertsBeforeChange?.forEach(a => a?.pin?.setMap(null));

    this.cluster?.clearMarkers();     //Vidage cluster
    this.alerts?.forEach(alert => {   //Pour chaque alerte
      alert.pin?.setMap(null);          //On enleve la pin de la carte
      this.createMarker(alert);         //On (re)crée la pin

      if (this.clusterEnabled) {        //Si on est en mode Cluster
        this.cluster?.addMarker(alert.pin); //On ajoute la pin au cluster
      } else {
        if (this.map?.googleMap !== undefined) {
          alert.pin?.setMap(this.map?.googleMap); //Sinon on ajoute la pin sur la map directement
        }
      }
    });
  }

  /**
   * Fonction de création de la pin de l'alerte
   * @param alert 
   */
  createMarker(alert: Alert) {

    //Coordonnées de l'alerte
    const myLatlng = new google.maps.LatLng(alert.lat, alert.lng);
    let pinUrl = undefined;

    //Background pin selon le status
    if (alert.status === "IN PROGRESS") {
      pinUrl = "/assets/img/pins/pin_in-progress_blue.svg";
    } else if (alert.status === "IGNORED") {
      pinUrl = "/assets/img/pins/pin_ignore_grey.svg";
    } else if (alert.status === "TERMINATED") {
      pinUrl = "/assets/img/pins/pin_completed_green.svg";
    } else {
      pinUrl = "/assets/img/pins/pin_on-hold_red.svg";
    }

    //Pin de couleur
    const pin = {
      url: pinUrl,
      scaledSize: new google.maps.Size(182 * this.GMAPS_MULTIPLIER, 182 * this.GMAPS_MULTIPLIER),
      origin: new google.maps.Point(0,0),
      anchor: new google.maps.Point(34 * this.GMAPS_MULTIPLIER, 180 * this.GMAPS_MULTIPLIER)
    };

    //Création de la Pin
    alert.pin = new google.maps.Marker({
      position: myLatlng,
      title: alert.title,
      icon: pin,
      zIndex: this.zIndexCurrent
    });

    this.zIndexCurrent++;

    //Logo modèle
    let icon;
    if (alert.icon !== null && alert.icon !== undefined) {        
      icon = {
        url: alert.icon,
        scaledSize: new google.maps.Size(94 * this.GMAPS_MULTIPLIER, 94 * this.GMAPS_MULTIPLIER),
        origin: new google.maps.Point(0,0),
        anchor: new google.maps.Point(-8 * this.GMAPS_MULTIPLIER, 158 * this.GMAPS_MULTIPLIER)
      };
    } else if (alert.fa_icon !== null) {
      const faType: string = alert.fa_icon.split(' ')[0];
      const faName: string = alert.fa_icon.split(' ').pop().substring(3);
      let faTypeArray: FaIconRaw[] = [];
      switch (faType) {
        case 'fas':
          faTypeArray = falist.solid as FaIconRaw[];
          break;
        case 'far':
          faTypeArray = falist.regular as FaIconRaw[];
          break;
        case 'fal':
          faTypeArray = falist.light as FaIconRaw[];
          break;
        case 'fad':
          faTypeArray = falist.solid as FaIconRaw[];
          break;
        case 'fab':
          faTypeArray = falist.brands as FaIconRaw[];
          break;
      }

      const fa_icon = faTypeArray.find(item => item.name === faName);      
      if (fa_icon !== undefined) {        
        icon = {
          path: fa_icon.path, //SVG path
          fillColor: alert.fa_color, //color of the marker
          fillOpacity: 1,
          strokeWeight: 0,
          scale: 0.055, //size of the marker, careful! this scale also affects anchor and labelOrigin
          origin: new google.maps.Point(-300 * this.GMAPS_MULTIPLIER, 2880 * this.GMAPS_MULTIPLIER),
          anchor: new google.maps.Point(-300 * this.GMAPS_MULTIPLIER, 2880 * this.GMAPS_MULTIPLIER)
        }
      } else {
        icon = ' ';
      }
    }

    //Seconde Pin pour afficher le logo modèle
    alert.pinLogo = new google.maps.Marker({
      position: myLatlng,
      title: alert.title,
      icon: icon,
      optimized: alert.icon !== null ? false : true,
      zIndex: this.zIndexCurrent  //Le Z Index de la Pin logo modèle est supérieur à celui de la Pin status
    });

    this.zIndexCurrent++;

    //On bind les positions des pins
    alert.pin.bindTo("position", alert.pinLogo);

    //On bind la map de la 2eme pin sur la map de la 1ere
    //Afin que lorsque la première pin est affichée sur la map, la seconde pin s'affiche également
    alert.pinLogo.bindTo("map", alert.pin);

    //Avec un handler pour détecter le click sur la pin
    alert.pin.addListener("click", () => {
      //Sélection de l'alerte cliquée
      this.alertSelected.emit(alert);
    });

    //Avec un handler pour détecter le click sur le logo
    alert.pinLogo.addListener("click", () => {
      //Sélection de l'alerte cliquée
      this.alertSelected.emit(alert);
    });

  }

  /**
   * Fonction pour activer/désactiver le mode cluster
   * @param event 
   */
  toggleCluster(event: any) {
    this.clusterEnabled = event.target.checked;
    this.refreshMarkers();
  }

  toggleAutoCenter(event: any) {
    this.authCenterChanged.emit(event.target.checked);
  }


  /**
   * Fonction pour ajouter des custom controls sur la GoogleMap
   * (checkbox pour regrouper les alertes)
   * @param controlDiv 
   * @param map 
   */
  addCustomControls(controlDiv: Element, map: google.maps.Map) {
    // Set CSS for the control border.
    const controlUI = document.createElement("div");

    controlUI.style.backgroundColor = "#fff";
    controlUI.style.border = "2px solid #fff";
    controlUI.style.borderRadius = "3px";
    controlUI.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    controlUI.style.cursor = "pointer";
    controlUI.style.marginTop = "8px";
    controlUI.style.marginBottom = "25px";
    controlUI.style.textAlign = "left";
    controlDiv.appendChild(controlUI);

    
    // Set CSS for the control interior.
    const controlInput = document.createElement("input");
    controlInput.setAttribute("type", "checkbox");
    controlInput.setAttribute("checked", "checked");
    controlInput.addEventListener("change", ($e) => { this.toggleCluster($e) });
    controlUI.appendChild(controlInput);

    const controlLabel = document.createElement("label");
    controlLabel.innerHTML = "Group alerts by cluster";
    controlLabel.style.color = "rgb(25,25,25)";
    controlLabel.style.fontFamily = "Roboto,Arial,sans-serif";
    controlLabel.style.fontSize = "14px";
    controlLabel.style.lineHeight = "0px";
    controlLabel.style.paddingLeft = "5px";
    controlLabel.style.paddingRight = "5px";

    controlUI.appendChild(controlLabel);

    /////////////////////////////////////:
    // Set CSS for the control border.
    // const autoCentercontrolUI = document.createElement("div");

    // autoCentercontrolUI.style.backgroundColor = "#fff";
    // autoCentercontrolUI.style.border = "2px solid #fff";
    // autoCentercontrolUI.style.borderRadius = "3px";
    // autoCentercontrolUI.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    // autoCentercontrolUI.style.cursor = "pointer";
    // autoCentercontrolUI.style.marginTop = "5px";
    // autoCentercontrolUI.style.marginBottom = "22px";
    // autoCentercontrolUI.style.textAlign = "left";
    // controlDiv.appendChild(autoCentercontrolUI);

    
    // Set CSS for the control interior.
    // const autoCentercontrolInput = document.createElement("input");
    // autoCentercontrolInput.setAttribute("type", "checkbox");
    // autoCentercontrolInput.setAttribute("checked", "checked");
    // autoCentercontrolInput.addEventListener("change", ($e) => { this.toggleAutoCenter($e) });
    // autoCentercontrolUI.appendChild(autoCentercontrolInput);

    // const autoCentercontrolLabel = document.createElement("label");
    // autoCentercontrolLabel.innerHTML = this.translateService.instant("alerts.settings.autocenter_on_alert");
    // autoCentercontrolLabel.style.color = "rgb(25,25,25)";
    // autoCentercontrolLabel.style.fontFamily = "Roboto,Arial,sans-serif";
    // autoCentercontrolLabel.style.fontSize = "14px";
    // autoCentercontrolLabel.style.lineHeight = "0px";
    // autoCentercontrolLabel.style.paddingLeft = "5px";
    // autoCentercontrolLabel.style.paddingRight = "5px";

    // autoCentercontrolUI.appendChild(autoCentercontrolLabel);
    
  }

}
